import React, { useMemo, useState } from 'react'
import Tippy from '@tippyjs/react'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { PostFormat, deleteProvider, usePost } from 'sharedKernel/api'
import { getHubRoute } from 'sharedKernel/handlers'
import { ROUTES } from 'sharedKernel/constants'
import { trans } from 'sharedKernel/i18n'
import { Filter } from 'components/Filter'
import { OrderBy, FilterOrderByProps } from 'components/OrderBy'
import { FilterOrderBar } from 'sharedKernel/styledComponents/filterComponents/style'
import { showModal } from 'components/popup'
import { showToast } from 'components/toast'
import { Loading } from 'components/loading'
import { followCursor } from 'tippy.js'
import { MeetingRoomList } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/MeetingRoomList'
import {
  MEETING_ROOM_API,
  MEETING_ROOM_LIST_SORT,
} from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/constants'
import { buildMeetingRoomListPayload } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/listRequest'
import { normalizeMeetingRoomListResponse } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/mappers'
import {
  GearButton,
  ManageMeetingRoomEmptyIconWrap,
  ManageMeetingRoomEmptyRoot,
  ManageMeetingRoomEmptyText,
  MeetingRoomOrderAndButtonRow,
  PageHeader,
  PageTitle,
} from './styles'

export const HubCompanyManageMeetingRoomPage = (): JSX.Element => {
  const navigate = useNavigate()

  const orderByOptions = [
    { label: trans('meetingRoom.list.orderRecent'), value: MEETING_ROOM_LIST_SORT.recent },
    { label: trans('meetingRoom.list.orderAlphabetical'), value: MEETING_ROOM_LIST_SORT.name },
  ]

  const [selectedOrderBy, setSelectedOrderBy] = useState<FilterOrderByProps>(orderByOptions[0])
  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')

  const listRequest: PostFormat = useMemo(
    () => ({
      url: MEETING_ROOM_API.ALL,
      data: buildMeetingRoomListPayload({
        searchTerm: appliedSearchTerm,
        sort: selectedOrderBy.value,
      }),
    }),
    [appliedSearchTerm, selectedOrderBy.value]
  )

  const { data, isFetching, refetch } = usePost<unknown>(
    listRequest,
    `meeting-rooms-all_${appliedSearchTerm}_${selectedOrderBy.value}`
  )

  const rooms = useMemo(() => normalizeMeetingRoomListResponse(data), [data])

  const { mutate: deleteMeetingRoom } = deleteProvider(async () => {
    showToast('success', trans('meetingRoom.toast.deleteSuccess'))
    await refetch()
  })

  const onSearch = (searchTerm: string): void => {
    setCurrentSearchTerm(searchTerm)
    setAppliedSearchTerm(searchTerm)
  }

  const onOrderBy = (selected: FilterOrderByProps): void => {
    setSelectedOrderBy(selected)
  }

  const handleDelete = (id: string): void => {
    const room = rooms.find(r => r.id === id)
    const name = room?.name ?? trans('meetingRoom.list.thisSpace')

    showModal('double', {
      title: trans('meetingRoom.list.deleteTitle'),
      message: `${trans('meetingRoom.list.deleteMessagePrefix')} "${name}"?`,
      buttons: [
        { text: trans('meetingRoom.common.yes'), value: 'yes' },
        { text: trans('meetingRoom.common.no'), value: 'no' },
      ],
      action: () => {
        deleteMeetingRoom({ url: MEETING_ROOM_API.byId(id) })
      },
    })
  }

  const emptyMessage =
    appliedSearchTerm === ''
      ? trans('meetingRoom.list.emptyNone')
      : trans('meetingRoom.list.emptySearch')

  const hasRooms = rooms.length > 0
  const showList = !isFetching && hasRooms

  return (
    <>
      <PageHeader>
        <PageTitle>{trans('meetingRoom.list.pageTitle')}</PageTitle>
        <Tippy
          content={trans('meetingRoom.list.settingsHint')}
          followCursor={true}
          plugins={[followCursor]}
          animation="scale"
          arrow={false}
          theme="primary"
        >
          <div>
            <GearButton
              variant="light"
              aria-label={trans('meetingRoom.list.settingsHint')}
              onClick={() => navigate(getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_SETTINGS))}
            >
              <FontAwesomeIcon icon={['fas', 'gear']} />
            </GearButton>
          </div>
        </Tippy>
      </PageHeader>

      <FilterOrderBar>
        <Filter currentSearchTerm={currentSearchTerm} btnClick={onSearch} />
        <MeetingRoomOrderAndButtonRow>
          <OrderBy
            selectedValue={selectedOrderBy}
            onSelect={onOrderBy}
            options={orderByOptions}
          />
          <Button
            variant="primary"
            className="fw-bold"
            onClick={() => navigate(getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_NEW))}
          >
            <FontAwesomeIcon icon={['fas', 'plus']} className="me-2" />
            {trans('meetingRoom.list.newSpace')}
          </Button>
        </MeetingRoomOrderAndButtonRow>
      </FilterOrderBar>

      {isFetching ? (
        <Loading />
      ) : showList ? (
        <MeetingRoomList
          rooms={rooms}
          onDelete={handleDelete}
        />
      ) : (
        <ManageMeetingRoomEmptyRoot>
          <ManageMeetingRoomEmptyIconWrap aria-hidden>
            <FontAwesomeIcon icon={['fas', 'door-open']} style={{ fontSize: '5rem' }} />
          </ManageMeetingRoomEmptyIconWrap>
          <ManageMeetingRoomEmptyText>{emptyMessage}</ManageMeetingRoomEmptyText>
        </ManageMeetingRoomEmptyRoot>
      )}
    </>
  )
}
